// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, ref, watch } from 'vue'
import { useStore } from '@nanostores/vue'
import { useElementVisibility } from '@vueuse/core'
import { isLogin } from '@/store/userStore'
import {
	type HeroSectionData,
	type HeroButtonType,
} from '@/components/HeroSection.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { type RegistrationCardData } from '@/components/RegistrationCard.vue'
import type { StepData } from '@/types/StepCheckout'
import type { WarehouseCardData } from '@/types/WareHouse'
import { getProductSharingUrl } from '@/legacy/ts/productSharing'
import hybridPage from '@/lib/hybridPage'
import { getCreditText, cn, formatSrc, setCurrentGaPageName } from '@/lib/utils'
import { useTooltip } from '@/lib/useTooltip'
import {
	createGAEvent,
	GaGeneralPageName,
	GaGeneralEvent,
} from '@/assets/ts/customEvent'
import getRedirectSeoLink from '@/lib/getSeoRedirect'

hybridPage()

createApp({
	name: 'Shipping B2B',
	components: {
		BaseButton,
	},
	setup() {
		const isLoginGlobal = useStore(isLogin)
		const isCurrentPageNext = computed(() => {
			return window.pageContent?.currentPage === 'shipping-b2b-next'
		})
		const currentGaPageName = computed(() => {
			return isCurrentPageNext.value
				? GaGeneralPageName.ShippingB2BNext
				: GaGeneralPageName.ShippingB2B
		})

		setCurrentGaPageName(currentGaPageName.value)
		createGAEvent(GaGeneralEvent.PageLoad, {
			page_name: currentGaPageName.value,
		})

		// ================== flow start ==================
		const Flowtitle: string = window.pageContent.flowContent.title
		const Flowdata: StepData[] = window.pageContent.flowContent.content
		const { containerRef } = useTooltip(Flowdata)

		const DEFAULTITEM = 0
		const openIndex = ref(DEFAULTITEM)

		const isActiveStep = (index: number) => openIndex.value === index
		const summaryClasses = (index: number) =>
			cn(
				'p-20px flex flex-col gap-8px',
				isActiveStep(index)
					? 'bg-t-900'
					: 'bg-transparent hover:bg-b-5% active:bg-b-5%',
				isActiveStep(index)
					? 'rounded-md'
					: 'rounded-none hover:rounded-md active:rounded-md',
				isActiveStep(index) ? ' border-0' : ' border-b-n-200 border-b-2',
			)
		const stepClasses = (index: number) =>
			cn(
				'typography-s1-emp',
				isActiveStep(index) ? 'text-t-400' : 'text-t-600',
				'hover:bg-gray-200',
			)
		const titleClasses = (index: number) =>
			cn('typography-h5-str', isActiveStep(index) ? 'text-white' : 'text-black')

		/**
		 * Function to control the expansion or collapse of steps.
		 * Clicking an already expanded step does nothing, ensuring one step is always open.
		 */
		const toggleDetail = (index: number): void => {
			// If a non-current step is clicked, switch to that step
			if (openIndex.value !== index) {
				openIndex.value = index
			}
			// If the current expanded step is clicked, do nothing (ensuring at least one step is open)
		}

		// ================== end flow ==================

		const heroSectionData: HeroSectionData = window.pageContent.heroContent
		const currentHeroSectionData = computed(() => {
			return isLoginGlobal.value
				? heroSectionData.afterLogin
				: heroSectionData.beforeLogin
		})

		const promoBtnEl = ref<HTMLElement | null>(null)
		const isPromoVisible = useElementVisibility(promoBtnEl)
		const warehouseCardData: WarehouseCardData =
			window.pageContent.warehouseContent
		const currenWarehouseContent = computed(() => {
			return isLoginGlobal.value
				? warehouseCardData.afterLogin
				: warehouseCardData.beforeLogin
		})

		function handleHeroSectionButtonClick(buttonType: HeroButtonType) {
			if (buttonType === 'primary') {
				if (currentHeroSectionData.value.primaryIsRegister) {
					window.BsHeaderVm.showSignupPanel()
					createGAEvent(GaGeneralEvent.ButtonClick, {
						button_name: 'Sign Up - Hero',
						page_name: currentGaPageName.value,
					})
				} else {
					window.location.href =
						currentHeroSectionData.value.primaryBtnLink || ''
				}
			} else if (buttonType === 'secondary') {
				window.location.href =
					currentHeroSectionData.value.secondaryBtnLink || ''
			}
		}

		const currentPromoBtn = computed(() => {
			return getCreditText(currentHeroSectionData.value.promoBtn || '')
		})

		const handleClickWarehouse = (link?: string) => {
			if (link) {
				createGAEvent(GaGeneralEvent.ButtonClick, {
					button_name: 'Pricing Calculator',
					page_name: currentGaPageName.value,
				})
				window.location.href = link
			} else {
				const url = `${import.meta.env.PUBLIC_ROOT_URL}/${getRedirectSeoLink(
					'warehouses',
				)}`
				window.location.href = url
			}
		}

		watch(isPromoVisible, isVisible => {
			if (isVisible) {
				promoBtnEl.value?.classList?.add('animate-horizontal-shake')
			} else {
				promoBtnEl.value?.classList?.remove('animate-horizontal-shake')
			}
		})

		// ---------------------
		// Registration Card Section Start
		// ---------------------
		const registrationCardData: RegistrationCardData =
			window.pageContent.registrationContent

		const currentRegistrationCardContent = computed(() => {
			return isLoginGlobal.value
				? registrationCardData.afterLogin
				: registrationCardData.beforeLogin
		})

		const currentRegistrationCardTitle1 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title1)
		})

		const currentRegistrationCardTitle2 = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.title2)
		})

		const WELCOME_CREDIT = window.BUYANDSHIP?.settings?.member_welcome_credits

		const currentRegistrationCardDesc = computed(() => {
			return getCreditText(currentRegistrationCardContent.value.description)
		})

		const bgRegistrationCardClass = computed(() => {
			return currentRegistrationCardContent.value.class
		})

		const openReferral = (link?: string) => {
			if (link) {
				window.location.href = link
				return
			}
			window.location.href = getProductSharingUrl('referral')
		}

		const openRegister = () => {
			window.BsHeaderVm.showSignupPanel()
		}

		const handleRegistrationCardClick = (link?: string) => {
			if (isLoginGlobal.value) {
				openReferral(link)
			} else {
				openRegister()
			}
		}

		// ---------------------
		// Service Content
		// ---------------------

		const currentServiceContent = window.pageContent.serviceContent

		// ---------------------
		// Registration Card Section End
		// ---------------------

		const contentOtherService = window.pageContent.otherServiceContent

		// only show other service in japan region from pm issue (charile 2024-03-01)
		const { isShowOtherService } = window.pageContent.otherServiceContent

		return {
			WELCOME_CREDIT,
			isLoginGlobal,
			currentHeroSectionData,
			currentPromoBtn,
			currenWarehouseContent,
			promoBtnEl,
			handleHeroSectionButtonClick,
			handleClickWarehouse,
			currentRegistrationCardContent,
			currentRegistrationCardTitle1,
			currentRegistrationCardTitle2,
			currentRegistrationCardDesc,
			bgRegistrationCardClass,
			handleRegistrationCardClick,
			// ================== Flow start ==================
			containerRef,
			Flowtitle,
			Flowdata,
			summaryClasses,
			stepClasses,
			titleClasses,
			toggleDetail,
			cn,
			openIndex,
			// ================== Flow end ==================
			currentServiceContent,
			contentOtherService,
			isShowOtherService,
			formatSrc,
		}
	},
}).mount('#bsMain')
